import React, { useEffect, useRef } from 'react'
import { HelpCircle, X } from 'lucide-react'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Detect current theme and send to iframe
  const sendThemeToIframe = () => {
    if (iframeRef.current) {
      const isDarkMode = document.documentElement.classList.contains('dark') ||
                       document.body.classList.contains('dark') ||
                       window.getComputedStyle(document.body).getPropertyValue('background-color').includes('26, 32, 44')
      
      iframeRef.current.contentWindow?.postMessage({
        type: 'theme-update',
        isDarkMode
      }, '*')
    }
  }

  // Send theme when modal opens
  useEffect(() => {
    if (isOpen) {
      // Wait for iframe to load, then send theme
      const timer = setTimeout(() => {
        sendThemeToIframe()
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl border border-green-100 dark:border-gray-700">
          {/* Modal Header */}
          <div className="sticky top-0 bg-gradient-to-r from-green-100/90 via-emerald-100/90 to-teal-100/90 dark:from-gray-800 dark:to-gray-900 backdrop-blur-md border-b border-green-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl z-10">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-green-700 to-emerald-600 bg-clip-text text-transparent flex items-center space-x-2">
                <HelpCircle className="w-6 h-6 text-emerald-500" />
                <span>SmartClean Help Guide</span>
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-2 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
                title="Close help"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Modal Content with Scroll */}
          <div className="max-h-[80vh] overflow-y-auto">
            <iframe
              ref={iframeRef}
              src="/HELP_GUIDE.html"
              className="w-full h-[80vh] border-0 bg-white/50 dark:bg-gray-800/50"
              title="SmartClean Help Guide"
              sandbox="allow-scripts allow-same-origin allow-forms"
              onLoad={sendThemeToIframe}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
